import {
  CREATE_PDF_REPORT_TEMPLATE_URL,
  GENERATE_PDF_REPORT_URL,
  UPDATE_PDF_REPORT_TEMPLATE_URL,
  UPLOAD_PDF_REPORT_LOGO_URL,
} from "@/utils/constants/constants";
import axios from "axios";

import {
  build_pdf_report_config_payload,
  build_template_payload,
} from "./formal_document_template_helpers";
import { build_review_validation_message } from "./formal_document_template_wizard_helpers";

const get_first_error_message = (value) => {
  if (typeof value === "string" && value.trim()) {
    return value;
  }
  if (Array.isArray(value)) {
    return get_first_error_message(value[0]);
  }
  if (value && typeof value === "object") {
    return get_first_error_message(Object.values(value)[0]);
  }
  return "";
};

const build_one_off_payload = (component, config_payload, admin_signature) => {
  const payload = {
    config: config_payload,
    entity_profile_id: component.target_entity_profile_id,
    application_id: component.target_application_id,
  };
  if (admin_signature?.image_data_url) {
    payload.admin_signature = admin_signature;
  }
  return payload;
};

export default {
  handle_logo_selection(event) {
    const logo_file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!logo_file) {
      return;
    }
    this.is_dragging_logo = false;
    this.sync_logo_preview_url(URL.createObjectURL(logo_file));
    this.upload_logo(logo_file);
  },
  handle_logo_drag_state(is_dragging) {
    this.is_dragging_logo = is_dragging;
  },
  handle_logo_drop(event) {
    const logo_file = event.dataTransfer?.files?.[0] || null;
    this.is_dragging_logo = false;
    if (!logo_file) {
      return;
    }
    this.sync_logo_preview_url(URL.createObjectURL(logo_file));
    this.upload_logo(logo_file);
  },
  sync_logo_preview_url(next_preview_url) {
    this.clear_logo_preview_url();
    this.logo_preview_url = next_preview_url;
  },
  clear_logo_preview_url() {
    const current_preview_url = String(this.logo_preview_url || "");
    if (current_preview_url.startsWith("blob:")) {
      URL.revokeObjectURL(current_preview_url);
    }
    this.logo_preview_url = "";
  },
  upload_logo(logo_file) {
    const form_data = new FormData();
    form_data.append("project_id", this.project_id);
    form_data.append("logo_file", logo_file);
    this.uploading_logo = true;
    axios
      .post(UPLOAD_PDF_REPORT_LOGO_URL, form_data, {
        headers: { ...this.headers, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        this.apply_uploaded_logo(response.data.data || {});
      })
      .catch((error) => {
        this.show_error(error.response?.data?.message || "Failed to upload the logo.");
      })
      .finally(() => {
        this.uploading_logo = false;
      });
  },
  clear_logo() {
    this.template_form.logo_url = "";
    this.clear_logo_preview_url();
  },
  apply_uploaded_logo(payload) {
    this.template_form.logo_url = payload.logo_url || "";
    if (payload.logo_preview_url) {
      this.sync_logo_preview_url(payload.logo_preview_url);
    }
  },
  save_template() {
    const validation_message = build_review_validation_message(this.template_form);
    if (validation_message) {
      this.show_info(validation_message);
      return;
    }
    const template_payload = build_template_payload(this.template_form);
    const config_payload = build_pdf_report_config_payload(this.template_form);
    this.generated_document = null;
    if (this.template_form.save_as_template === false) {
      if (this.pdf_requires_admin_signature()) {
        this.open_signature_modal({ config_payload });
        return;
      }
      this.begin_one_off_generation(config_payload);
      return;
    }
    this.saving_template = true;
    this.show_loader("Saving PDF template...").then((loader) => {
      this.submit_reusable_template(template_payload, loader);
    });
  },
  submit_reusable_template(payload, loader) {
    axios
      .post(this.get_save_url(), payload, { headers: this.headers })
      .then((response) => {
        this.show_success("PDF template saved successfully.");
        this.$emit("saved", response.data.data);
      })
      .catch((error) => {
        this.handle_save_error(error);
      })
      .finally(() => {
        this.saving_template = false;
        this.close_loader(loader);
      });
  },
  submit_one_off_template(payload, loader, admin_signature = null) {
    if (!this.target_entity_profile_id) {
      this.show_error("One-off PDFs can only be generated for a specific profile.");
      this.saving_template = false;
      this.close_loader(loader);
      return;
    }
    axios
      .post(
        GENERATE_PDF_REPORT_URL,
        build_one_off_payload(this, payload, admin_signature),
        { headers: this.headers }
      )
      .then((response) => {
        const generated_document = response.data.data || null;
        this.generated_document = generated_document;
        this.show_success("PDF report generated successfully.");
        this.$emit("generated", generated_document);
        this.open_generated_document();
      })
      .catch((error) => {
        this.handle_save_error(error);
      })
      .finally(() => {
        this.saving_template = false;
        this.close_loader(loader);
      });
  },
  handle_save_error(error) {
    const message = error.response?.data?.message || "Failed to save the PDF template.";
    const serializer_errors = error.response?.data?.errors;
    this.show_error(get_first_error_message(serializer_errors) || message);
  },
  get_save_url() {
    return this.is_editing
      ? UPDATE_PDF_REPORT_TEMPLATE_URL
      : CREATE_PDF_REPORT_TEMPLATE_URL;
  },
};
